module.exports = {
  title: 'Rentable vs. Usable Square Feet: What’s the Difference? ',
  pageTitle: 'Rentable vs. Usable Square Feet: What’s the Difference? | CBRE Spacer',
  metaTagText: 'Confused by RSF and USF? Here’s what each term means, why it matters when leasing office space and how to use both figures in your search.',
  preview: `
    When you start searching for office space, you will 
    quickly encounter two different measurements for 
    the same building: rentable square feet (RSF) and
    usable square feet (USF). They describe different
    things and confusing them is a common and costly 
    mistake.
    Here is what each term means and why it matters.  
  `,
  content: `
    <p>
    When you start searching for office space, you will quickly encounter 
    two different measurements for the same building: rentable square feet (RSF)
    and usable square feet (USF). They describe different things and confusing 
    them is a common and costly mistake.
    </p>
    <p>
    Here is what each term means and why it matters. 
    </p>
    <h2>The basics: SF and NSF</h2>
    <p>Before the more complex terms, two simple ones: </p>
      <ul>
        <li>
          SF – a square foot, 12 inches by 12 inches.
        </li>
        <li>
          NSF – Net Square Feet, the amount of space a thing itself occupies. A chair with
          a 2x2 footprint would be a 4 NSF item.
        </li>
      </ul>

    <h2>USF – Usable Square Feet</h2>
    <p>
    Every item in an office needs space around it. Desks need room to push back a
    chair. Doors need room to swing. Corridors connect rooms. When you add up the
    NSF of everything in a space plus all the circulation space needed to make the 
    layout work, you get the USF. 
    </p>
    <p>In simple terms: USF = NSF + Circulation. </p>
    <p>
    USF is the measurement used by interior designers and space planners — the 
    direct measure of how much space you can occupy and arrange. You may also hear
    it called Carpetable Area. It excludes columns, windowsills and other 
    non-carpetable elements.
    </p>
    <p>When CBRE’s office space calculator gives you a result in usable square feet, 
    this is the number your space planner will work from to design your layout. </p>

 
    <h2>RSF – Rentable Square Feet</h2>
    <p>
    Office buildings contain areas that every tenant benefits from but does not 
    exclusively occupy: lobbies, elevator banks, shared corridors and building 
    amenities. Landlords charge rent on these shared areas proportionally — 
    which is why the figure you pay rent on is almost always larger than the 
    space you actually use. 
    </p>
    <p>
    RSF = USF + your share of building common areas.
    </p>
    <p>
    The ratio between RSF and USF is determined by the building’s loss factor.
    A loss factor of 15% means that for every 100 USF you occupy, you pay rent 
    on 115 RSF. Loss factors vary by building and market. In dense urban markets
    like New York City, they can reach 25–30%. In newer or more efficient buildings,
    they are typically lower. 
    </p>

    <h2>Which number should you use?</h2>

    <p>Use RSF when searching for space and comparing listings — it is the 
    figure landlords quote and the one your lease will be based on. </p>

    <p>Use USF when planning your layout — it tells you how much space you actually 
    have to work with.</p>

    <p>CBRE’s office space calculator gives you both. When you complete the quiz, 
    you can toggle between RSF and USF results depending on which figure you need.</p>

    <p>If you want to understand the loss factor for a specific building you’re 
    considering, a CBRE broker can walk you through the numbers and help you 
    compare buildings on a like-for-like basis. </p>

  `
};
